// src/components/admin/Settings/AdminSettings.tsx
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Settings, Bot, Shield, Database } from 'lucide-react';
import AIAssistantSettings from './AIAssistantSettings';

type SettingsTab = 'general' | 'ai' | 'auth';

export const AdminSettings: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Determine active tab from URL
  const getActiveTab = (): SettingsTab => {
    if (location.pathname.includes('/ai')) return 'ai';
    if (location.pathname.includes('/auth')) return 'auth';
    return 'general';
  };

  const [activeTab, setActiveTab] = useState<SettingsTab>(getActiveTab());

  const tabs = [
    { id: 'general' as SettingsTab, label: 'General', icon: <Settings size={18} />, path: '/admin/settings' },
    { id: 'ai' as SettingsTab, label: 'AI Assistant', icon: <Bot size={18} />, path: '/admin/settings/ai' },
    { id: 'auth' as SettingsTab, label: 'Authentication', icon: <Shield size={18} />, path: '/admin/settings/auth' }
  ];

  const handleTabChange = (tab: SettingsTab, path: string) => {
    setActiveTab(tab);
    navigate(path);
  };

  const renderGeneralSettings = () => (
    <div className="settings-section">
      <h3>General Settings</h3>
      <p className="settings-description">Configure general application settings.</p>

      <div className="settings-card">
        <div className="settings-row">
          <div className="settings-field">
            <label>Application Name</label>
            <input type="text" defaultValue="CHEM2131 Virtual Lab" />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-field">
            <label>Institution</label>
            <input type="text" defaultValue="Memorial University" />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-field">
            <label>Contact Email</label>
            <input type="email" defaultValue="vlab@mun.ca" />
          </div>
        </div>
      </div>

      <div className="settings-card">
        <h4><Database size={16} /> Database</h4>
        <div className="settings-row">
          <div className="settings-info">
            <span className="info-label">Connection Status</span>
            <span className="info-value success">Connected</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAuthSettings = () => (
    <div className="settings-section">
      <h3>Authentication Settings</h3>
      <p className="settings-description">Configure OAuth providers and authentication options.</p>

      <div className="settings-card">
        <h4>Google OAuth</h4>
        <div className="settings-row">
          <div className="settings-toggle">
            <label>
              <input type="checkbox" defaultChecked />
              <span>Enable Google Sign-In</span>
            </label>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-field">
            <label>Client ID</label>
            <input type="text" placeholder="Google OAuth Client ID" />
          </div>
        </div>
      </div>

      <div className="settings-card">
        <h4>Microsoft Azure AD</h4>
        <div className="settings-row">
          <div className="settings-toggle">
            <label>
              <input type="checkbox" defaultChecked />
              <span>Enable Microsoft Sign-In</span>
            </label>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-field">
            <label>Tenant ID</label>
            <input type="text" placeholder="Azure AD Tenant ID" />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-field">
            <label>Client ID</label>
            <input type="text" placeholder="Azure AD Client ID" />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="admin-settings">
      <div className="settings-header">
        <h1>Settings</h1>
      </div>

      <div className="settings-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id, tab.path)}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="settings-content">
        {activeTab === 'general' && renderGeneralSettings()}
        {activeTab === 'ai' && <AIAssistantSettings />}
        {activeTab === 'auth' && renderAuthSettings()}
      </div>

      <style>{`
        .admin-settings {
          padding: 24px 32px;
          color: #fff;
          max-width: 1200px;
        }

        .settings-header {
          margin-bottom: 24px;
        }

        .settings-header h1 {
          font-size: 24px;
          font-weight: 700;
          margin: 0;
        }

        .settings-tabs {
          display: flex;
          gap: 4px;
          border-bottom: 1px solid rgba(255,255,255,0.1);
          margin-bottom: 24px;
        }

        .settings-tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: transparent;
          border: none;
          color: #888;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
          transition: all 0.15s;
        }

        .settings-tab:hover {
          color: #fff;
        }

        .settings-tab.active {
          color: #a5b4fc;
          border-bottom-color: #6366f1;
        }

        .settings-content {
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .settings-section h3 {
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 8px;
        }

        .settings-description {
          color: #888;
          font-size: 14px;
          margin: 0 0 20px;
        }

        .settings-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 16px;
        }

        .settings-card h4 {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 600;
          color: #a5b4fc;
          margin: 0 0 16px;
        }

        .settings-row {
          margin-bottom: 16px;
        }

        .settings-row:last-child {
          margin-bottom: 0;
        }

        .settings-field label {
          display: block;
          font-size: 12px;
          font-weight: 500;
          color: #888;
          margin-bottom: 6px;
        }

        .settings-field input {
          width: 100%;
          max-width: 400px;
          padding: 10px 14px;
          background: rgba(0,0,0,0.3);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 8px;
          color: #fff;
          font-size: 14px;
        }

        .settings-field input:focus {
          outline: none;
          border-color: #6366f1;
        }

        .settings-toggle label {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          font-size: 14px;
        }

        .settings-toggle input[type="checkbox"] {
          width: 18px;
          height: 18px;
          accent-color: #6366f1;
        }

        .settings-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .info-label {
          font-size: 14px;
          color: #888;
        }

        .info-value {
          font-size: 14px;
          font-weight: 500;
        }

        .info-value.success {
          color: #10b981;
        }
      `}</style>
    </div>
  );
};

export default AdminSettings;
